/* SubProcessorsPage — /legal/subprocessors
 *
 * Single source of truth for who else processes customer data. Update
 * this page whenever a new vendor is introduced — the DPA references
 * it by URL so customers can object before a change takes effect.
 */
import LegalLayout from '../../components/legal/LegalLayout';

const SUBPROCESSORS = [
  {
    name: 'MongoDB Atlas',
    purpose: 'Primary database (customer + candidate records, signed documents)',
    location: 'United States (with EU regions available on request)',
    transfer: 'UK IDTA + SCCs',
    website: 'https://www.mongodb.com/legal/privacy-policy',
  },
  {
    name: 'Emergent Labs',
    purpose: 'Application hosting and ingress (TLS termination)',
    location: 'United States',
    transfer: 'UK IDTA + SCCs',
    website: 'https://emergent.sh',
  },
  {
    name: 'Resend',
    purpose: 'Transactional email delivery (preview / sandbox environments)',
    location: 'United States',
    transfer: 'UK IDTA + SCCs',
    website: 'https://resend.com/legal/privacy-policy',
  },
  {
    name: 'Amazon Web Services (SES)',
    purpose: 'Transactional email delivery (production)',
    location: 'EU-West (Ireland)',
    transfer: 'Adequacy decision — UK ↔ EU',
    website: 'https://aws.amazon.com/privacy',
  },
  {
    name: 'Bullhorn',
    purpose: 'ATS integration — only where the Customer has connected their Bullhorn account',
    location: 'United States / EU',
    transfer: 'UK IDTA + SCCs (where applicable)',
    website: 'https://www.bullhorn.com/privacy-policy/',
  },
];

export default function SubProcessorsPage() {
  return (
    <LegalLayout title="Sub-processor List" lastUpdated="8 February 2026" activePath="/legal/subprocessors">
      <p>
        This page lists the third parties HyreDocs uses to process personal data on behalf of our customers. It forms part of our <a href="/legal/dpa">Data Processing Agreement</a>. We notify customers at least 30 days before adding or replacing a sub-processor — see clause 7 of the DPA for the objection process.
      </p>

      <h2>Current sub-processors</h2>
      <table>
        <thead>
          <tr>
            <th>Sub-processor</th>
            <th>Purpose</th>
            <th>Location</th>
            <th>Transfer mechanism</th>
          </tr>
        </thead>
        <tbody>
          {SUBPROCESSORS.map((s) => (
            <tr key={s.name}>
              <td>
                <a href={s.website} target="_blank" rel="noreferrer">{s.name}</a>
              </td>
              <td>{s.purpose}</td>
              <td>{s.location}</td>
              <td>{s.transfer}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>How we evaluate sub-processors</h2>
      <ul>
        <li>Each vendor signs a written data-processing contract that imposes equivalent obligations to those we owe our customers.</li>
        <li>We assess each vendor&apos;s security certifications (SOC 2, ISO 27001, etc.) and incident-response history.</li>
        <li>We perform an annual review of every active sub-processor.</li>
        <li>We use the minimum data necessary for each integration — sub-processors do not receive a full copy of customer records unless their function requires it.</li>
      </ul>

      <h2>Subscribe to changes</h2>
      <p>
        Email <a href="mailto:contracts@hyredocs.com?subject=Subscribe%20-%20Subprocessor%20Updates">contracts@hyredocs.com</a> to be added to our sub-processor change notification list. You will receive at least 30 days&apos; notice before any change takes effect.
      </p>
    </LegalLayout>
  );
}
